import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
    Alert,
    Container,
    Typography,
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    Menu,
    MenuItem,
    Checkbox,
    Button,
    Breadcrumbs,
    Link,
    Chip,
    IconButton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    Avatar,
    AvatarGroup,
    Dialog,
    DialogTitle,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Flag from 'react-world-flags';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TranslateIcon from '@mui/icons-material/Translate';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CircularProgress from '@mui/material/CircularProgress';
import { FileIcon, defaultStyles } from 'react-file-icon';
import FolderIcon from '@mui/icons-material/Folder';
import { useSelector } from 'react-redux';
import axios from 'axios';
import isoLanguages from './isoLanguages';
import { getProjectStatus, statusColor, getProjectProgress } from '../utils/projectStatus';
import FolderSelector from './FolderSelector';
import PreTranslateModal from './PreTranslateModal';

const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === code?.toLowerCase()) || {
        name: code,
        countryCode: 'UN',
    };

const getSupportedServices = (code) => {
    const lang = isoLanguages.find((l) => l.code.toLowerCase() === code?.toLowerCase());
    return lang?.services || ['deepl', 'google', 'openai'];
};

const getFileExtension = (name = '') =>
    name.includes('.') ? name.split('.').pop().toLowerCase() : '';

const renderFileIcon = ({ type, name }) => {
    if (type === 'folder') return <FolderIcon color="primary" />;
    const ext = getFileExtension(name);
    if (defaultStyles[ext]) {
        return (
            <Box sx={{ width: 32 }}>
                <FileIcon extension={ext} {...defaultStyles[ext]} />
            </Box>
        );
    }
    return (
        <Box sx={{ width: 32 }}>
            <FileIcon extension={ext || 'file'} />
        </Box>
    );
};

const getInitial = (str) =>
    typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() : '';

export default function ProjectDetails() {
    const { id } = useParams();
    const token = useSelector((state) => state.auth.token);
    const decodedToken = token ? jwtDecode(token) : null;
    const currentUserId = decodedToken?.user?.id || decodedToken?.id;
    const navigate = useNavigate();
    const location = useLocation();

    const [project, setProject] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [glossaries, setGlossaries] = useState([]);
    const [filteredGlossaries, setFilteredGlossaries] = useState([]);
    const [languageData, setLanguageData] = useState({});
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [uniqueSegmentCount, setUniqueSegmentCount] = useState(0);
    const [memorySegmentCount, setMemorySegmentCount] = useState(0);
    const [projectFile, setProjectFile] = useState({ type: 'file', name: '' });
    const [languages, setLanguages] = useState([]);
    const [selectedDocVersion, setSelectedDocVersion] = useState(0);
    const [translatorDialog, setTranslatorDialog] = useState({ open: false, pid: null, selected: [] });
    const [addLangDialog, setAddLangDialog] = useState({ open: false, lang: '' });
    const [downloadMenu, setDownloadMenu] = useState({ anchorEl: null, projectId: null });
    const [downloadStatus, setDownloadStatus] = useState({ projectId: null, mode: null });
    const [downloadMessage, setDownloadMessage] = useState({ open: false, text: '', severity: 'success' });

    // Folder Selector State
    const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);
    const [folderSelectorMode, setFolderSelectorMode] = useState('internal'); // 'internal' | 'google' | 'dropbox'
    const [exportTarget, setExportTarget] = useState({ projectId: null, fileType: 'translated' }); // Store what we are exporting
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [isDropboxConnected, setIsDropboxConnected] = useState(false);
    const [isOneDriveConnected, setIsOneDriveConnected] = useState(false);
    const [preTranslateModalOpen, setPreTranslateModalOpen] = useState(false);
    const [isPreTranslating, setIsPreTranslating] = useState(false);
    const [preTranslateProgress, setPreTranslateProgress] = useState(0);



    // Build breadcrumbs from URL
    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, idx, arr) => (
            <Link
                key={path}
                color={idx === arr.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, idx + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
                {path}
            </Link>
        ));

    // Compute word/char counts
    const calculateCounts = useCallback((sections = []) => {
        let totalWords = 0;
        let totalChars = 0;
        sections.forEach((section) => {
            const raw = section || '';
            totalWords += raw.split(/\s+/).filter(Boolean).length;
            totalChars += raw.length;
        });
        setWordCount(totalWords);
        setCharCount(totalChars);
    }, []);

    const fetchProject = useCallback(
        (projId) => {
            axios
                .get(`/api/projects/${projId}`, { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => {
                    const p = res.data.project;
                    setProject(p);
                    setUniqueSegmentCount(res.data.uniqueSegmentsCount ?? 0);
                    calculateCounts(p.sections || []);
                    const path = p.fileUrl || p.templateFilePath || '';
                    setProjectFile({ type: 'file', name: path.split('/').pop() || p.projectName });
                    setMemorySegmentCount(res.data.memorySegmentsCount ?? 0);
                    const langs = res.data.languages || [{ code: p.targetLanguage, projectId: p._id }];
                    setLanguages(langs);
                    setSelectedDocVersion(p.currentVersion || 0);
                    const data = {};
                    langs.forEach((l) => {
                        data[l.projectId] = null; // placeholder
                    });
                    setLanguageData(data);
                })
                .catch((err) => console.error('Error fetching project:', err));

            // Check integration status
            axios.get('/api/integrations/status', { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                    if (res.data.google) setIsGoogleConnected(true);
                    if (res.data.dropbox) setIsDropboxConnected(true);
                    if (res.data.onedrive) setIsOneDriveConnected(true);
                })
                .catch(err => console.error('Error checking integrations:', err));

        },
        [token, calculateCounts]
    );

    useEffect(() => {
        if (!token) return;
        fetchProject(id);
    }, [id, token, fetchProject]);

    // load team and glossaries
    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setTeamMembers(res.data))
            .catch((err) => console.error('Team fetch error:', err));

        axios
            .get('/api/glossary', { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setGlossaries(res.data))
            .catch((err) => console.error('Glossary fetch error:', err));
    }, [token]);

    useEffect(() => {
        if (!project) return;
        const filtered = glossaries.filter(
            (g) =>
                g.sourceLanguage.toLowerCase() === project.sourceLanguage.toLowerCase() &&
                g.targetLanguage.toLowerCase() === project.targetLanguage.toLowerCase()
        );
        setFilteredGlossaries(filtered);
    }, [glossaries, project]);

    useEffect(() => {
        if (!token || languages.length === 0) return;
        Promise.all(
            languages.map((l) =>
                axios.get(`/api/projects/${l.projectId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            )
        )
            .then((results) => {
                const data = {};
                results.forEach((res) => {
                    data[res.data.project._id] = res.data.project;
                });
                setLanguageData(data);
            })
            .catch((err) => console.error('Error fetching language projects:', err));
    }, [languages, token]);

    const handleOpenDownloadMenu = (event, projectId) => {
        setDownloadMenu({ anchorEl: event.currentTarget, projectId });
    };

    const handleCloseDownloadMenu = () => {
        setDownloadMenu({ anchorEl: null, projectId: null });
    };

    const handleDownload = async (projectId, fileType = 'translated') => {
        const mode = 'download';
        setDownloadStatus({ projectId, mode });
        try {
            const response = await axios.get(
                `/api/export/${projectId}/download?type=${fileType}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );
            const cd = response.headers['content-disposition'];
            let fileName = `${projectId}-${fileType}`;
            if (cd) {
                const m = cd.match(/filename="?([^";]+)"?/);
                if (m) fileName = m[1];
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setDownloadMessage({
                open: true,
                text: 'Translated document downloaded successfully.',
                severity: 'success',
            });
        } catch (err) {
            console.error('Download error:', err);
            setDownloadMessage({
                open: true,
                text: 'Unable to download the translated document. Please try again.',
                severity: 'error',
            });
        } finally {
            setDownloadStatus({ projectId: null, mode: null });
        }
    };

    // Open Folder Selector
    const initiateExport = (mode, fileType = 'translated') => {
        setExportTarget({ projectId: downloadMenu.projectId, fileType });
        setFolderSelectorMode(mode);
        setFolderSelectorOpen(true);
        handleCloseDownloadMenu();
    };

    const handleFolderSelect = async (folderId) => {
        setFolderSelectorOpen(false);
        const { projectId, fileType } = exportTarget;
        if (!projectId) return;

        // Perform the export
        await handleExport(projectId, fileType, folderSelectorMode, folderId);
    };

    const handleExport = async (projectId, fileType, mode, folderId) => {
        setDownloadStatus({ projectId, mode });
        try {
            // 1. Get the file blob from backend
            const response = await axios.get(
                `/api/export/${projectId}/download?type=${fileType}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );

            const cd = response.headers['content-disposition'];
            let fileName = `${projectId}-${fileType}`;
            if (cd) {
                const m = cd.match(/filename="?([^";]+)"?/);
                if (m) fileName = m[1];
            }
            const fileBlob = new Blob([response.data], { type: response.data.type });

            // 2. Upload to destination
            const uploadData = new FormData();
            uploadData.append('file', new File([fileBlob], fileName, { type: fileBlob.type }));
            uploadData.append('parent', folderId || (mode === 'google' ? 'root' : '')); // Handle root mappings

            let uploadUrl = '/api/files/upload'; // Internal default
            if (mode === 'google') {
                uploadUrl = '/api/integrations/google/upload';
            } else if (mode === 'dropbox') {
                uploadUrl = '/api/dropbox/upload';
                // Dropbox upload expects 'parentPath' in body, not 'parent'
                uploadData.delete('parent');
                uploadData.append('parentPath', folderId || '');
            } else if (mode === 'onedrive') {
                uploadUrl = '/api/integrations/onedrive/upload';
            }

            await axios.post(uploadUrl, uploadData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setDownloadMessage({
                open: true,
                text: `Successfully exported to ${mode === 'google' ? 'Google Drive' : (mode === 'dropbox' ? 'Dropbox' : (mode === 'onedrive' ? 'OneDrive' : 'File Manager'))}.`,
                severity: 'success',
            });

        } catch (err) {
            console.error('Export error:', err);
            setDownloadMessage({
                open: true,
                text: `Failed to export to ${mode === 'google' ? 'Google Drive' : (mode === 'dropbox' ? 'Dropbox' : (mode === 'onedrive' ? 'OneDrive' : 'File Manager'))}.`,
                severity: 'error',
            });
        } finally {
            setDownloadStatus({ projectId: null, mode: null });
        }
    };

    const handleLaunchPreTranslate = async (options) => {
        setIsPreTranslating(true);
        setPreTranslateProgress(0);
        const { useTM, tmConfirmation, useAI, selectedProjects } = options;

        try {
            for (let i = 0; i < selectedProjects.length; i++) {
                const { projectId, service } = selectedProjects[i];

                // 1. Fetch full project details (sections)
                const res = await axios.get(`/api/projects/${projectId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const lp = res.data.project;
                const sections = lp.sections || [];
                const srcLang = lp.sourceLanguage;
                const tgtLang = lp.targetLanguage;

                // 2. Prepare arrays
                const fullTranslated = [...(lp.translatedSections || new Array(sections.length).fill(''))];
                const fullConfidences = [...(lp.confidenceScores || new Array(sections.length).fill(0))];
                const fullSources = [...(lp.translationSources || new Array(sections.length).fill(''))];
                const approvedStatus = [...(lp.approvedSections || [])];

                // 3. Find segments that need translation
                // Dedupe for efficiency
                const uniqueSegments = [];
                const mapping = [];
                const normText = (t) => (t || '').trim().toLowerCase();

                sections.forEach((text, idx) => {
                    const norm = normText(text);
                    if (!norm) {
                        mapping.push(-1);
                        return;
                    }
                    // Only translate if empty
                    if (fullTranslated[idx] && fullTranslated[idx].trim() !== '') {
                        mapping.push(-2); // already translated
                        return;
                    }

                    let uIdx = uniqueSegments.findIndex(s => normText(s) === norm);
                    if (uIdx === -1) {
                        uIdx = uniqueSegments.length;
                        uniqueSegments.push(text);
                    }
                    mapping.push(uIdx);
                });

                if (uniqueSegments.length > 0) {
                    const uniqueTranslations = new Array(uniqueSegments.length).fill('');
                    const uniqueConfidences = new Array(uniqueSegments.length).fill(0);
                    const uniqueSources = new Array(uniqueSegments.length).fill('');

                    for (let ui = 0; ui < uniqueSegments.length; ui++) {
                        const originalText = uniqueSegments[ui];
                        let translated = '';
                        let confidence = 0;
                        let source = '';

                        // TM check
                        if (useTM) {
                            try {
                                const mem = await axios.post(
                                    '/api/translation-memory/check',
                                    { userId: currentUserId, originalText, sourceLanguage: srcLang, targetLanguage: tgtLang },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (mem.data.translatedText) {
                                    translated = mem.data.translatedText;
                                    confidence = mem.data.confidenceScore || 0;
                                    source = 'memory';
                                }
                            } catch (err) {
                                console.error('TM check failed for segment', ui, err);
                            }
                        }

                        // AI Fallback
                        if (!translated && useAI) {
                            try {
                                const endpoint = service === 'openai' ? '/api/openai/translate' : '/api/translate';
                                const aiRes = await axios.post(
                                    endpoint,
                                    { text: originalText, sourceLanguage: srcLang, targetLanguage: tgtLang, service, tone: lp.tone || 'default' },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                );
                                translated = aiRes.data.translatedText;
                                confidence = 100;
                                source = 'ai';
                            } catch (err) {
                                console.error('AI translation failed for segment', ui, err);
                            }
                        }

                        if (translated) {
                            uniqueTranslations[ui] = translated;
                            uniqueConfidences[ui] = confidence;
                            uniqueSources[ui] = source;
                        }
                    }

                    // Map back
                    mapping.forEach((uIdx, idx) => {
                        if (uIdx >= 0 && uniqueTranslations[uIdx]) {
                            fullTranslated[idx] = uniqueTranslations[uIdx];
                            fullConfidences[idx] = uniqueConfidences[uIdx];
                            fullSources[idx] = uniqueSources[uIdx];

                            // Handle TM confirmation
                            if (uniqueSources[uIdx] === 'memory' && tmConfirmation === 'confirmed') {
                                if (!approvedStatus.includes(idx)) {
                                    approvedStatus.push(idx);
                                }
                            }
                        }
                    });

                    // Save
                    await axios.put(
                        `/api/projects/${projectId}/translate`,
                        {
                            translatedSections: fullTranslated,
                            confidenceScores: fullConfidences,
                            translationSources: fullSources,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    // If TM update approved sections specifically
                    if (useTM && tmConfirmation === 'confirmed') {
                        await axios.put(
                            `/api/projects/${projectId}`,
                            { approvedSections: approvedStatus },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                    }
                }

                setPreTranslateProgress(Math.round(((i + 1) / selectedProjects.length) * 100));
            }

            setDownloadMessage({
                open: true,
                text: 'Pre-translation completed successfully.',
                severity: 'success',
            });
            fetchProject(id); // refresh data
        } catch (err) {
            console.error('Pre-translate error:', err);
            setDownloadMessage({
                open: true,
                text: 'Error during pre-translation. Some languages might have failed.',
                severity: 'error',
            });
        } finally {
            setIsPreTranslating(false);
            setPreTranslateModalOpen(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`/api/projects/${project._id}/upload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            fetchProject(id);
        } catch (err) {
            console.error('Upload error:', err);
            alert('Failed to upload new version');
        }
    };

    // assign translator
    const handleTranslatorChange = (pid, tids) => {
        axios
            .put(
                `/api/projects/${pid}/assign-translator`,
                { translatorIds: tids },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() =>
                setLanguageData((prev) => ({
                    ...prev,
                    [pid]: { ...prev[pid], assignedTo: tids },
                }))
            )
            .catch((err) => console.error('Assign error:', err));
    };

    const handleGlossaryChange = (pid, gid) => {
        axios
            .put(
                `/api/projects/${pid}`,
                { glossaryId: gid || null },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() =>
                setLanguageData((prev) => ({
                    ...prev,
                    [pid]: { ...prev[pid], glossaryId: gid || null },
                }))
            )
            .catch((err) => console.error('Glossary update error:', err));
    };

    const handleServiceChange = (pid, service) => {
        axios
            .put(
                `/api/projects/${pid}`,
                { translationService: service },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() =>
                setLanguageData((prev) => ({
                    ...prev,
                    [pid]: { ...prev[pid], translationService: service },
                }))
            )
            .catch((err) => console.error('Service update error:', err));
    };

    const handleToneChange = (pid, newTone) => {
        axios
            .put(
                `/api/projects/${pid}`,
                { tone: newTone },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() =>
                setLanguageData((prev) => ({
                    ...prev,
                    [pid]: { ...prev[pid], tone: newTone },
                }))
            )
            .catch((err) => console.error('Tone update error:', err));
    };

    const handleJobBoardChange = (pid, newValue) => {
        axios
            .put(
                `/api/projects/${pid}`,
                { isJobBoardOpen: newValue },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() =>
                setLanguageData((prev) => ({
                    ...prev,
                    [pid]: { ...prev[pid], isJobBoardOpen: newValue },
                }))
            )
            .catch((err) => console.error('Job Board update error:', err));
    };

    const openTranslatorDialog = (pid) => {
        const current = languageData[pid];
        const selected = current
            ? Array.isArray(current.assignedTo)
                ? current.assignedTo
                : current.assignedTo
                    ? [current.assignedTo]
                    : []
            : [];
        setTranslatorDialog({ open: true, pid, selected });
    };

    const closeTranslatorDialog = () => {
        setTranslatorDialog({ open: false, pid: null, selected: [] });
    };
    const saveTranslators = () => {
        if (translatorDialog.pid) {
            handleTranslatorChange(translatorDialog.pid, translatorDialog.selected);
        }
        closeTranslatorDialog();
    };

    const openAddLangDialog = () => {
        setAddLangDialog({ open: true, lang: '' });
    };

    const closeAddLangDialog = () => {
        setAddLangDialog({ open: false, lang: '' });
    };

    const addLanguage = () => {
        if (!addLangDialog.lang) return;
        axios
            .post(
                `/api/projects/${project._id}/add-language`,
                { targetLanguage: addLangDialog.lang },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(() => {
                fetchProject(id);
                closeAddLangDialog();
            })
            .catch((err) => console.error('Add language error:', err));
    };

    const deleteLanguage = (pid) => {
        if (!window.confirm('Delete this language project?')) return;
        axios
            .delete(`/api/projects/${pid}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => fetchProject(id))
            .catch((err) => console.error('Delete language error:', err));
    };

    if (!project) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography>Loading…</Typography>
            </Container>
        );
    }

    // derive some UI state
    const sections = Array.isArray(project.sections) ? project.sections : [];

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    onClick={() => navigate('/dashboard/projects')}
                    sx={{ cursor: 'pointer' }}
                >
                    <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Projects
                </Link>
                {breadcrumbs}
            </Breadcrumbs>

            <Box mb={3}>
                <Typography variant="h4" fontWeight={700}>
                    {project.projectName}
                </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Source Language
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Flag
                            code={getLanguageDetails(project.sourceLanguage).countryCode}
                            style={{ width: 22 }}
                        />
                        <Typography>{getLanguageDetails(project.sourceLanguage).name}</Typography>
                    </Box>
                </Grid>

                {/* Removed target language dropdown; languages displayed in table */}

                <Grid item xs={12} sm={6} md={4} display="flex" alignItems="center" gap={1}>
                    {renderFileIcon(projectFile)}
                    <Typography>{projectFile.name}</Typography>
                </Grid>

                {/* Stats Bar */}
                <Grid item xs={12}>
                    <Paper
                        elevation={1}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'grey.50',
                        }}
                    >
                        <Box textAlign="center">
                            <Typography variant="subtitle2" color="text.secondary">
                                Segments
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                                {sections.length}
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="subtitle2" color="text.secondary">
                                Unique Segments
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                                {uniqueSegmentCount}
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="subtitle2" color="text.secondary">
                                TM Matches
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                                {memorySegmentCount}
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="subtitle2" color="text.secondary">
                                Words
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                                {wordCount}
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="subtitle2" color="text.secondary">
                                Characters
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                                {charCount}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>


                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Owner
                    </Typography>
                    <Typography>
                        {project.createdBy?.firstName} {project.createdBy?.lastName}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Document Versions
                    </Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                        <Select
                            value={selectedDocVersion}
                            onChange={(e) => setSelectedDocVersion(Number(e.target.value))}
                        >
                            {(project.documentVersions || []).map((_, i) => (
                                <MenuItem key={i} value={i}>{i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Translation Versions
                    </Typography>
                    <Typography>
                        {((project.documentVersions || [])[selectedDocVersion]?.translationVersions || []).length}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" gap={2}>
                        <Button variant="outlined" onClick={() => navigate(`/dashboard/projects/${project._id}/tasks`)}>
                            Open Task Board
                        </Button>
                        <Button
                            variant={project.isJobBoardOpen ? "contained" : "outlined"}
                            color={project.isJobBoardOpen ? "success" : "primary"}
                            onClick={() => {
                                const newValue = !project.isJobBoardOpen;
                                axios.put(`/api/projects/${project._id}`, { isJobBoardOpen: newValue }, { headers: { Authorization: `Bearer ${token}` } })
                                    .then(() => {
                                        setProject({ ...project, isJobBoardOpen: newValue });
                                    })
                                    .catch(err => console.error('Error toggling job board:', err));
                            }}
                            disabled={currentUserId !== project.createdBy._id}
                        >
                            {project.isJobBoardOpen ? "Job Board: Open" : "Post to Job Board"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<TranslateIcon />}
                    onClick={() => setPreTranslateModalOpen(true)}
                >
                    Pre-Translate
                </Button>
                <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
                    Upload New Version
                    <input type="file" hidden onChange={handleUpload} />
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddLangDialog}>
                    Add Language
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: 'grey.100' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Job Board</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Translator</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Download</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Translate</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Delete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {languages.map((lang) => {
                            const lp = languageData[lang.projectId];
                            if (!lp) return null;
                            const prog = getProjectProgress(lp);
                            const filtered = glossaries.filter(
                                (g) =>
                                    g.sourceLanguage.toLowerCase() === lp.sourceLanguage.toLowerCase() &&
                                    g.targetLanguage.toLowerCase() === lp.targetLanguage.toLowerCase()
                            );
                            return (
                                <TableRow hover key={lang.projectId}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Flag code={getLanguageDetails(lp.targetLanguage).countryCode} style={{ width: 20 }} />
                                            <Typography variant="body2">{getLanguageDetails(lp.targetLanguage).name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={lp.isJobBoardOpen || false}
                                            onChange={(e) => handleJobBoardChange(lang.projectId, e.target.checked)}
                                            size="small"
                                            color="success"
                                            disabled={currentUserId !== project.createdBy._id}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={getProjectStatus(lp)}
                                            color={statusColor[getProjectStatus(lp)]}
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <LinearProgress variant="determinate" value={prog} sx={{ width: 80, borderRadius: 1 }} />
                                            <Typography variant="body2">{prog}%</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 30, height: 30 } }}>
                                                {(() => {
                                                    const ids = Array.isArray(lp.assignedTo) ? lp.assignedTo : lp.assignedTo ? [lp.assignedTo] : [];
                                                    const translators = teamMembers.filter((m) => ids.includes(m.userId._id));
                                                    return translators.map((t) => (
                                                        <Avatar
                                                            key={t.userId._id}
                                                            src={t.userId.avatarUrl ? `/api/users/avatars/${t.userId._id}/${t.userId.avatarUrl.split('/').pop()}` : ''}
                                                        >
                                                            {!t.userId.avatarUrl &&
                                                                (getInitial(t.userId.firstName) + getInitial(t.userId.lastName))}
                                                        </Avatar>
                                                    ));
                                                })()}
                                            </AvatarGroup>
                                            <IconButton size="small" color="primary" onClick={() => openTranslatorDialog(lang.projectId)}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip
                                            title={
                                                prog >= 100
                                                    ? 'Download translated file'
                                                    : 'Translation must reach 100% before download is available'
                                            }
                                        >
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={(event) => handleOpenDownloadMenu(event, lang.projectId)}
                                                    disabled={prog < 100 || downloadStatus.projectId === lang.projectId}
                                                    startIcon={
                                                        downloadStatus.projectId === lang.projectId ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <CloudDownloadIcon />
                                                        )
                                                    }
                                                    endIcon={<ArrowDropDownIcon />}
                                                >
                                                    Download
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => navigate(`/dashboard/projects/${lang.projectId}/translate?docVersion=${selectedDocVersion}`)} color="primary">
                                            <TranslateIcon />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => deleteLanguage(lang.projectId)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={downloadMenu.anchorEl}
                open={Boolean(downloadMenu.anchorEl)}
                onClose={handleCloseDownloadMenu}
            >
                <MenuItem
                    onClick={() => {
                        const projectId = downloadMenu.projectId;
                        handleCloseDownloadMenu();
                        if (projectId) {
                            handleDownload(projectId, 'translated');
                        }
                    }}
                >
                    <ListItemIcon><CloudDownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download to Device</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => initiateExport('internal', 'translated')}
                >
                    <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Save to File Manager...</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => initiateExport('google', 'translated')} disabled={!isGoogleConnected}>
                    <ListItemIcon>
                        <img src="/assets/logo/googledrive-icon.png" alt="Drive" width="20" />
                    </ListItemIcon>
                    <ListItemText>Export to Google Drive</ListItemText>
                </MenuItem>
                {isDropboxConnected && (
                    <MenuItem onClick={() => initiateExport('dropbox', 'translated')}>
                        <ListItemIcon>
                            <img src="/assets/logo/dropbox-logo.png" alt="Dropbox" width="20" style={{ objectFit: 'contain' }} />
                        </ListItemIcon>
                        <ListItemText>Export to Dropbox</ListItemText>
                    </MenuItem>
                )}
                {isOneDriveConnected && (
                    <MenuItem onClick={() => initiateExport('onedrive', 'translated')}>
                        <ListItemIcon>
                            <img src="/assets/logo/onedrive-logo.png" alt="OneDrive" width="20" style={{ objectFit: 'contain' }} />
                        </ListItemIcon>
                        <ListItemText>Export to OneDrive</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Folder Selector Dialog */}
            <FolderSelector
                open={folderSelectorOpen}
                mode={folderSelectorMode}
                onClose={() => setFolderSelectorOpen(false)}
                onSelect={handleFolderSelect}
            />

            <Dialog open={translatorDialog.open} onClose={closeTranslatorDialog}>
                <DialogTitle>Select Translators</DialogTitle>
                <Box p={2} sx={{ minWidth: 300 }}>
                    <FormControl fullWidth>
                        <InputLabel>Translators</InputLabel>
                        <Select
                            multiple
                            value={translatorDialog.selected}
                            onChange={(e) =>
                                setTranslatorDialog((d) => ({
                                    ...d,
                                    selected: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                                }))
                            }
                            renderValue={(selected) => selected.map((id) => {
                                const member = teamMembers.find((m) => m.userId._id === id);
                                return member ? member.userId.email : '';
                            }).join(', ')}
                        >
                            {teamMembers.map((member) => (
                                <MenuItem key={member.userId._id} value={member.userId._id}>
                                    <Checkbox checked={translatorDialog.selected.indexOf(member.userId._id) > -1} />
                                    <ListItemText primary={`${member.userId.firstName || ''} ${member.userId.lastName || ''}`} secondary={member.userId.email} />
                                </MenuItem>
                            ))}
                            <MenuItem value="unassign">Unassign All</MenuItem>
                        </Select>
                    </FormControl>
                    <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={closeTranslatorDialog}>Cancel</Button>
                        <Button variant="contained" onClick={saveTranslators}>Save</Button>
                    </Box>
                </Box>
            </Dialog>

            <Dialog open={addLangDialog.open} onClose={closeAddLangDialog}>
                <DialogTitle>Add Language</DialogTitle>
                <Box p={2} sx={{ minWidth: 300 }}>
                    <FormControl fullWidth>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={addLangDialog.lang}
                            onChange={(e) =>
                                setAddLangDialog((d) => ({ ...d, lang: e.target.value }))
                            }
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Select language</em>
                            </MenuItem>
                            {isoLanguages
                                .filter(
                                    (l) =>
                                        !languages.some(
                                            (gl) => gl.code.toLowerCase() === l.code.toLowerCase()
                                        ) &&
                                        l.code.toLowerCase() !== project.sourceLanguage.toLowerCase()
                                )
                                .map((lang) => (
                                    <MenuItem key={lang.code} value={lang.code}>
                                        <Flag
                                            code={lang.countryCode}
                                            style={{ marginRight: '10px', width: '20px' }}
                                        />
                                        {lang.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={closeAddLangDialog}>Cancel</Button>
                        <Button variant="contained" onClick={addLanguage} disabled={!addLangDialog.lang}>
                            Add
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <Snackbar
                open={downloadMessage.open}
                autoHideDuration={4000}
                onClose={() => setDownloadMessage((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={downloadMessage.severity} variant="filled">
                    {downloadMessage.text}
                </Alert>
            </Snackbar>

            {/* Controls moved into table rows */}
            <PreTranslateModal
                open={preTranslateModalOpen}
                onClose={() => setPreTranslateModalOpen(false)}
                languages={languages.map(l => {
                    const lp = languageData[l.projectId] || {};
                    const details = getLanguageDetails(lp.targetLanguage || l.code);
                    return {
                        projectId: l.projectId,
                        code: lp.targetLanguage || l.code,
                        name: details.name,
                        countryCode: details.countryCode,
                        translationService: lp.translationService,
                        tone: lp.tone,
                        glossaryId: lp.glossaryId
                    };
                })}
                onLaunch={handleLaunchPreTranslate}
                isLaunching={isPreTranslating}
            />
        </Container >
    );
}
